import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import {
  MapPin, DollarSign, TrendingUp, AlertTriangle, Sparkles,
  Building2, BarChart3, CheckCircle2, Home,
} from 'lucide-react';

const fmtMoney = (v, { compact = false, sign = false } = {}) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const n = Number(v);
  const p = sign && n > 0 ? '+' : '';
  if (compact) {
    if (Math.abs(n) >= 1e6) return `${p}$${(n / 1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3) return `${p}$${(n / 1e3).toFixed(0)}K`;
  }
  return `${p}$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const fmtPct = (v, d = 1) =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : `${Number(v).toFixed(d)}%`;

function matchBadge(score) {
  if (score >= 85) return { bg: 'bg-emerald-500', text: 'text-white', ring: 'ring-emerald-300/50' };
  if (score >= 70) return { bg: 'bg-[#45C04A]', text: 'text-white', ring: 'ring-emerald-200/50' };
  if (score >= 50) return { bg: 'bg-amber-500', text: 'text-white', ring: 'ring-amber-200/50' };
  return { bg: 'bg-slate-400', text: 'text-white', ring: 'ring-slate-200/50' };
}

/**
 * Deal Scanner listing card. Displays scanner-only estimates (quick_metrics)
 * with a clear "estimate" disclaimer — the RendPleX Intelligence Score is
 * only ever computed on Analyze Deal via the existing engine.
 */
export default function DealCard({ result, onAnalyze, onSave, onShare, top }) {
  const { t } = useTranslation();
  const qm = result.quick_metrics || {};
  const badge = matchBadge(result.deal_match_score || 0);

  return (
    <div
      data-testid={`scanner-deal-card-${result.listing_id}`}
      className={`rounded-2xl overflow-hidden bg-white dark:bg-[#131824] border ${
        top ? 'border-[#45C04A] shadow-xl ring-2 ring-[#45C04A]/20' : 'border-slate-200 dark:border-slate-800'
      } hover:border-[#45C04A] transition`}
    >
      {top && (
        <div className="bg-gradient-to-r from-[#12355B] to-[#1565C0] text-white text-xs uppercase tracking-wider font-bold py-1.5 px-4 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" />
          {t('dealScanner.topMatch')}
        </div>
      )}

      <div className="relative aspect-[16/10] bg-slate-200 dark:bg-slate-800 overflow-hidden">
        {result.photo_url ? (
          <img
            src={result.photo_url}
            alt={result.address}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <Home className="w-10 h-10" />
          </div>
        )}
        {/* Deal Match badge */}
        <div className={`absolute top-3 right-3 ${badge.bg} ${badge.text} rounded-full px-3 py-1 text-sm font-bold shadow-lg ring-4 ${badge.ring}`}>
          {t('dealScanner.dealMatch')}: {result.deal_match_score || 0}%
        </div>
        {/* Demo tag */}
        {result.is_demo && (
          <div className="absolute top-3 left-3 bg-amber-500/95 text-white uppercase text-[10px] tracking-wider font-bold rounded px-2 py-0.5">
            {t('dealScanner.sampleTag')}
          </div>
        )}
        {result.already_in_portfolio && (
          <div className="absolute bottom-3 left-3 bg-[#12355B]/95 text-white text-xs font-semibold rounded-full px-3 py-1 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {t('dealScanner.alreadyInPortfolio')}
          </div>
        )}
      </div>

      <div className="p-5 space-y-4">
        {/* Address */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                <MapPin className="w-3.5 h-3.5" />
                {result.city}{result.borough ? ` · ${result.borough}` : ''}
              </div>
              <h3 className="font-semibold text-[#12355B] dark:text-white mt-0.5 truncate">
                {result.address}
              </h3>
            </div>
            <div className={`text-xs font-bold rounded px-2 py-0.5 ${badge.bg.replace('bg-', 'text-')} bg-transparent`}>
              {result.deal_match_band}
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-slate-700 dark:text-slate-300">
              <Building2 className="w-3 h-3" /> {t(`dealScanner.types.${result.property_type === 'five_plus' ? 'fivePlus' : result.property_type === 'apartment_building' ? 'apartmentBuilding' : result.property_type === 'mixed_use' ? 'mixedUse' : result.property_type}`, result.property_type)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-slate-700 dark:text-slate-300">
              {result.num_units} {t('dealScanner.units')}
            </span>
          </div>
        </div>

        {/* Price */}
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-2xl font-bold text-[#12355B] dark:text-white">
              {fmtMoney(result.asking_price)}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5">
              {t('dealScanner.askingPrice')}
            </div>
          </div>
        </div>

        {/* Financial estimates */}
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> {t('dealScanner.cashFlow')}
            </div>
            <div className={`text-sm font-bold ${qm.monthly_cash_flow > 0 ? 'text-emerald-600' : qm.monthly_cash_flow < 0 ? 'text-rose-500' : 'text-slate-500'}`}>
              {qm.monthly_cash_flow != null ? fmtMoney(qm.monthly_cash_flow, { sign: true }) + '/mo' : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <BarChart3 className="w-3 h-3" /> {t('dealScanner.capRate')}
            </div>
            <div className="text-sm font-bold text-[#12355B] dark:text-white">
              {fmtPct(qm.cap_rate)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> {t('dealScanner.coc')}
            </div>
            <div className="text-sm font-bold text-[#12355B] dark:text-white">
              {fmtPct(qm.cash_on_cash)}
            </div>
          </div>
        </div>

        {result.insufficient_data && (
          <div className="text-[11px] italic text-amber-600 dark:text-amber-400">
            {t('dealScanner.insufficientData')}
          </div>
        )}

        {/* Why selected */}
        {result.why_selected && (
          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/30 p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-400 mb-1">
              {t('dealScanner.whySelected')}
            </div>
            <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed">{result.why_selected}</p>
          </div>
        )}

        {/* Warnings */}
        {result.warnings?.length > 0 && (
          <ul className="space-y-1 text-xs text-amber-700 dark:text-amber-300">
            {result.warnings.slice(0, 3).map((w, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {w}
              </li>
            ))}
          </ul>
        )}

        {/* Estimate disclaimer */}
        <p className="text-[10px] text-slate-400 italic">
          {t('dealScanner.estimateDisclaimer')}
        </p>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            data-testid={`scanner-analyze-btn-${result.listing_id}`}
            onClick={() => onAnalyze(result)}
            className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-lg px-4 py-2 text-sm font-semibold flex-1"
          >
            {t('dealScanner.analyzeDeal')}
          </Button>
          <Button
            data-testid={`scanner-share-btn-${result.listing_id}`}
            onClick={() => onShare(result)}
            variant="outline"
            className="rounded-lg px-3 py-2 text-sm"
          >
            {t('dealScanner.share')}
          </Button>
        </div>
      </div>
    </div>
  );
}
